import { useContext, useState } from "react";
import { ToastContext } from "../contexts/ToastContext";
import useUploadMedia from "./useUploadMedia";
import useDisplayServerError from "./useDisplayServerError";
import { Categories, CollectionUpdatePayload, DecoratedCollection, ListSetUpdatePayload, UpdateCollectionPayload } from "../types/SharedTypes";
import { useCreateListSet, useUpdateCollection, useUpdateListPostsInListSet, useUpdateListSet } from "../api/data-access";
import { CreateCollectionForm } from "../components/createList/CreateListModal";

const useHandleUpdateCollection = (onSuccess?: () => void) => {
  
  const {requestToast} = useContext(ToastContext);
  const [isLoading, setIsLoading] = useState(false);

  const updateCollectionMutation = useUpdateCollection();
  const updateListPostsInSetMutation = useUpdateListPostsInListSet();
  const updateListSet = useUpdateListSet();
  const createListSet = useCreateListSet();
  
  const {handleMedia} = useUploadMedia();
  const {handleError} = useDisplayServerError();
  
  const updateCollectionSimple = async (update: CollectionUpdatePayload, collectionId: string, callOnSuccess: boolean = true) => {
    const updatedList: UpdateCollectionPayload = {id: collectionId, update};
    setIsLoading(true);
    try {
      const res = await updateCollectionMutation.mutateAsync(updatedList);
      requestToast('Updated collection.', 'success');
      setIsLoading(false);
      onSuccess && callOnSuccess && onSuccess();
      return res;
    } catch (err) {
      setIsLoading(false);
      handleError(err);
    }
  }
  
  const updateCollection = async (collectionForm: CreateCollectionForm, collection: DecoratedCollection, newImage?: File) => {
    
    setIsLoading(true);
    
    let image = collectionForm.image;
    let listSet: string | undefined = undefined;
    
    if (newImage) {
      const newMedia = await handleMedia(newImage);
      if (!newMedia) {
        console.log('Failed to update image.');
        setIsLoading(false);
        return;
      }
      image = newMedia.url;
    }
    
    if (collectionForm.isSet && collection.listSet) {
      //update smart collection
      const update: ListSetUpdatePayload = {
        isDisabled: collectionForm.isSetDisabled || false,
        period: collectionForm.period,
        startingKey: collectionForm.startingKey || 1
      }
      try {
        const res = await updateListSet.mutateAsync({id: collection.listSet._id, update});
      } catch (err) {
        handleError(err);
        setIsLoading(false);
        return;
      }
    }
    else if (collectionForm.isSet && collectionForm.startingKey) {
      //converting an existing collection to smart
      try {
        listSet = await createListSet.mutateAsync({
          period: collectionForm.period || 'none',
          startingKey: collectionForm.startingKey,
          defaults: {
            description: collectionForm.description,
            image
          }
        });
      } catch (err) {
        requestToast("Failed to create smart collection, try again.", "error");
        return;
      }
    }
    
    const update: CollectionUpdatePayload = {
      title: collectionForm.title,
      description: collectionForm.description,
      category: collectionForm.category as Categories,
      subCategories: collectionForm.subCategories || [],
      isPublic: collectionForm.isPublic,
      isDraft: collection.isDraft,
      image,
      listSet
    }
    
    const updatedList: UpdateCollectionPayload = {id: collection._id, update};
    
    updateCollectionMutation.mutate(updatedList, {
      onSuccess() {
        requestToast('Updated collection.', 'success');
        setIsLoading(false);
        onSuccess && onSuccess();
      },
      onError: (err) => {
        handleError(err);
        setIsLoading(false);
      }
    });
  }
  
  const updateListPostsInSet = async (update: CollectionUpdatePayload, setId: string) => {
    
    setIsLoading(true);
    const updatedList: UpdateCollectionPayload = {id: setId, update};
    try {
      await updateListPostsInSetMutation.mutateAsync(updatedList);
      requestToast('Updated entire collection.', 'success');
      setIsLoading(false);
      return true;
    } catch (err) {
      setIsLoading(false);
      handleError(err);
    }
  }

  const updateListSetDefaults = async (description: string | undefined, image: string | undefined, setId: string) => {
    setIsLoading(true);
    try {
      await updateListSet.mutateAsync({id: setId, update: {
        defaults: {description, image}
      }});
      requestToast("Defaults updated for smart collection.", "success");
      setIsLoading(false);
      onSuccess && onSuccess();
    } catch (err) {
      setIsLoading(false);
      handleError(err);
    }
  }
  
  return {updateCollection, updateCollectionSimple, updateListPostsInSet, isLoading, updateListSetDefaults}
}
export default useHandleUpdateCollection;